import { useRef } from 'react';
import { toPng } from 'html-to-image';
import { getNodesBounds, getViewportForBounds } from '@xyflow/react';
import { useStore, selectActiveNodes, selectActiveEdges } from '../store';
import { deserialize, downloadText, serialize } from '../exports';
import { exportNetlistXlsx, exportSpecXlsx, exportWiringXlsx } from '../excelExports';
import { computeNets, buildNetlistRows } from '../nets';
import { exportProjectPdf } from '../pdfExport';
import { showToast } from './Toast';
import { requirePro } from '../entitlements';

/**
 * Reusable shell of project-level operations: save / load / new + the three
 * export entry points (PNG, wiring xlsx, spec+BOM xlsx). Used by both the
 * top-bar menus AND the project tree's context menus so the two surfaces stay
 * in sync.
 *
 * The hook returns ready-to-bind callbacks plus a hidden file-input element
 * used by the "Open" action; render `fileInputElement` somewhere in the tree.
 */
export function useProjectActions() {
  const meta = useStore((s) => s.meta);
  const nodes = useStore(selectActiveNodes);
  const edges = useStore(selectActiveEdges);
  const setProject = useStore((s) => s.setProject);
  const newProject = useStore((s) => s.newProject);
  const fileRef = useRef<HTMLInputElement>(null);

  const safeName = (meta.name || 'skema').replace(/[^a-z0-9-_]+/gi, '_');

  const onSave = () => {
    const state = useStore.getState();
    downloadText(
      `${safeName}.skema.json`,
      serialize(state.meta, state.sheets, state.activeSheetId),
    );
    showToast('Project saved', 'success');
  };

  const onLoadClick = () => fileRef.current?.click();

  const onLoadFile = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    const inputEl = e.target;
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      try {
        const parsed = deserialize(String(reader.result));
        // Opening a file starts a different project — detach the active cloud
        // row first so its contents aren't overwritten by the loaded file.
        window.dispatchEvent(new CustomEvent('skema:cloud-detach'));
        setProject({
          meta: parsed.meta,
          sheets: parsed.sheets,
          activeSheetId: parsed.activeSheetId,
        });
        showToast(`Loaded ${file.name}`, 'success');
      } catch (err) {
        showToast(`Failed to load: ${(err as Error).message}`, 'error');
      } finally {
        // Electron's native Open dialog leaves the renderer's focus stuck
        // on the (display:none) file input. Subsequent text inputs visually
        // get a focus ring on click but silently swallow every keystroke
        // until something jolts focus back to the window — the user's
        // workaround was Save → Open, which round-trips through another
        // focus-restoring code path. Do that explicitly here:
        //   1. Blur the file input.
        //   2. window.focus() so Electron knows the renderer wants keys.
        //   3. Click the canvas root once to seat focus there cleanly.
        inputEl.value = '';
        inputEl.blur();
        requestAnimationFrame(() => {
          window.focus();
          // Move focus to body so subsequent input clicks behave normally.
          if (document.body && typeof document.body.focus === 'function') {
            // Body needs tabIndex to be focusable; set it transiently.
            const prevTabIndex = document.body.getAttribute('tabindex');
            document.body.setAttribute('tabindex', '-1');
            document.body.focus();
            if (prevTabIndex === null) document.body.removeAttribute('tabindex');
            else document.body.setAttribute('tabindex', prevTabIndex);
          }
        });
      }
    };
    reader.readAsText(file);
  };

  const onNew = () => {
    if (nodes.length > 0 && !confirm('Discard current project?')) return;
    // Detach from any active cloud project BEFORE clearing the store, so the
    // cloud autosave doesn't overwrite that saved project with the empty one.
    window.dispatchEvent(new CustomEvent('skema:cloud-detach'));
    newProject();
    showToast('New project', 'info');
  };

  const onExportPng = async () => {
    const viewportEl = document.querySelector<HTMLElement>('.react-flow__viewport');
    if (!viewportEl) {
      showToast('Canvas not found — switch to a graph or mechanical sheet', 'error');
      return;
    }
    if (nodes.length === 0) {
      showToast('Nothing to export — add components first', 'info');
      return;
    }
    try {
      const padding = 80;
      const nodeBounds = getNodesBounds(nodes);
      let x1 = nodeBounds.x;
      let y1 = nodeBounds.y;
      let x2 = nodeBounds.x + nodeBounds.width;
      let y2 = nodeBounds.y + nodeBounds.height;

      const WIRE_MARGIN = 60;
      x1 -= WIRE_MARGIN;
      y1 -= WIRE_MARGIN;
      x2 += WIRE_MARGIN;
      y2 += WIRE_MARGIN;

      for (const e of edges) {
        const d = e.data as
          | { midX?: number; midY?: number; midX2?: number; midY2?: number }
          | undefined;
        const mx = d?.midX;
        const my = d?.midY;
        for (const bx of [d?.midX, d?.midX2]) {
          if (typeof bx === 'number') {
            x1 = Math.min(x1, bx - 20);
            x2 = Math.max(x2, bx + 20);
          }
        }
        for (const by of [d?.midY, d?.midY2]) {
          if (typeof by === 'number') {
            y1 = Math.min(y1, by - 20);
            y2 = Math.max(y2, by + 20);
          }
        }
        const lox = (e.data as { labelOffsetX?: number } | undefined)?.labelOffsetX;
        const loy = (e.data as { labelOffsetY?: number } | undefined)?.labelOffsetY;
        if (typeof mx === 'number' && typeof lox === 'number') {
          x1 = Math.min(x1, mx + lox - 40);
          x2 = Math.max(x2, mx + lox + 40);
        }
        if (typeof my === 'number' && typeof loy === 'number') {
          y1 = Math.min(y1, my + loy - 20);
          y2 = Math.max(y2, my + loy + 20);
        }
      }

      const bounds = { x: x1, y: y1, width: x2 - x1, height: y2 - y1 };
      const imageWidth = Math.ceil(bounds.width) + padding * 2;
      const imageHeight = Math.ceil(bounds.height) + padding * 2;
      const viewport = getViewportForBounds(bounds, imageWidth, imageHeight, 0.1, 2, 0.05);

      // The drawing sheet is always white paper — export on white too.
      const bgColor =
        getComputedStyle(document.documentElement)
          .getPropertyValue('--paper')
          .trim() || '#ffffff';

      const dataUrl = await toPng(viewportEl, {
        backgroundColor: bgColor,
        pixelRatio: 2,
        width: imageWidth,
        height: imageHeight,
        style: {
          width: `${imageWidth}px`,
          height: `${imageHeight}px`,
          transform: `translate(${viewport.x}px, ${viewport.y}px) scale(${viewport.zoom})`,
        },
      });

      const a = document.createElement('a');
      a.href = dataUrl;
      a.download = `${safeName}.png`;
      a.click();
      showToast('PNG exported', 'success');
    } catch (err) {
      showToast(`PNG export failed: ${(err as Error).message}`, 'error');
    }
  };

  const onExportWiring = async () => {
    if (!requirePro('Excel export')) return;
    const state = useStore.getState();
    // Aggregate ALL schematic sheets — not just the active one — so the export
    // is the same regardless of which sheet happens to be open.
    const schem = state.sheets.filter((s) => s.kind === 'schematic');
    const allNodes = schem.flatMap((s) => s.nodes);
    const allEdges = schem.flatMap((s) => s.edges);
    await exportWiringXlsx(meta, allNodes, allEdges, `${safeName}-wiring.xlsx`, state.sheets);
    showToast('Wiring .xlsx exported (with per-harness sheets)', 'success');
  };

  const onExportSpec = async () => {
    if (!requirePro('Excel export')) return;
    const state = useStore.getState();
    const schem = state.sheets.filter((s) => s.kind === 'schematic');
    const allNodes = schem.flatMap((s) => s.nodes);
    const allEdges = schem.flatMap((s) => s.edges);
    await exportSpecXlsx(meta, allNodes, allEdges, `${safeName}-spec.xlsx`, state.sheets);
    showToast('Spec .xlsx exported', 'success');
  };

  const onExportNetlist = async () => {
    if (!requirePro('Excel export')) return;
    const state = useStore.getState();
    const nets = computeNets(state.sheets);
    if (nets.length === 0) {
      showToast('No nets to export — wire some pins first', 'info');
      return;
    }
    await exportNetlistXlsx(buildNetlistRows(nets), `${safeName}-netlist.xlsx`);
    showToast(`Netlist exported (${nets.length} nets)`, 'success');
  };

  const onExportPdf = async () => {
    if (!requirePro('PDF export')) return;
    const state = useStore.getState();
    showToast('Building PDF — this may take a few seconds', 'info', 2400);
    try {
      await exportProjectPdf(meta, state.sheets, `${safeName}.pdf`);
      showToast('PDF exported (with title block)', 'success');
    } catch (err) {
      showToast(`PDF export failed: ${(err as Error).message}`, 'error');
    }
  };

  /** Hidden file input — render this somewhere so the "Open" callback works. */
  const fileInputElement = (
    <input
      ref={fileRef}
      type="file"
      accept=".json,application/json"
      style={{ display: 'none' }}
      onChange={onLoadFile}
    />
  );

  return {
    onSave,
    onLoadClick,
    onNew,
    onExportPng,
    onExportWiring,
    onExportSpec,
    onExportNetlist,
    onExportPdf,
    fileInputElement,
  };
}
